import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommandInterface } from '../interfaces/command.interface';
import { DEFAULT_COMMANDS } from '../config/command-registry.config';

@Injectable({
  providedIn: 'root'
})
export class ShortcutService implements OnDestroy {
  private readonly _router = inject(Router);
  private readonly _ngZone = inject(NgZone);

  private readonly _visible = signal<boolean>(false);
  private readonly _commands = signal<CommandInterface[]>(DEFAULT_COMMANDS);

  readonly visible = this._visible.asReadonly();
  readonly commands = this._commands.asReadonly();

  readonly visible$: Observable<boolean> = toObservable(this._visible);
  readonly commands$: Observable<CommandInterface[]> = toObservable(this._commands);

  private _subscription: Subscription | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    this._ngZone.runOutsideAngular(() => {
      this._subscription = fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(
          filter((event) => {
            if (!event.key) return false;
            const eventKey = event.key.toLowerCase();
            const isCtrlK = (event.ctrlKey || event.metaKey) && eventKey === 'k';

            // Check for registered command shortcuts
            if (!isCtrlK) {
              const command = this._commands().find((c) => {
                if (!c.keys) return false;
                const keys = c.keys.toLowerCase().split('.');
                const matchAlt = keys.includes('alt') === event.altKey;
                const matchShift = keys.includes('shift') === event.shiftKey;
                const matchCtrl = keys.includes('ctrl') === (event.ctrlKey || event.metaKey);
                const keyChar = keys[keys.length - 1];
                return matchAlt && matchShift && matchCtrl && eventKey === keyChar;
              });

              if (command) {
                event.preventDefault();
                this._ngZone.run(() => {
                  this.executeCommand(command);
                });
                return false; // Stop further processing
              }
            }

            return isCtrlK;
          })
        )
        .subscribe((event) => {
          if (this.shouldIgnoreEvent(event)) {
            return;
          }

          event.preventDefault();
          this._ngZone.run(() => {
            this.toggleVisibility();
          });
        });
    });
  }

  private shouldIgnoreEvent(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    const isInput =
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    // We only ignore if the user is typing in an input,
    // BUT typically Global Command Palette overrides this specific shortcut (Cmd+K)
    // because it's rarely used for text editing.
    // However, the requirements say "Não deve conflitar com Inputs ativos".
    // So if focus is on input, we check if we should block it.
    // Usually Ctrl+K in inputs might be "Hyperlink" in some editors, or Delete Line in others.
    // Let's Respect the requirement: "Não deve conflitar com Inputs ativos"
    return isInput;
  }

  public toggleVisibility(): void {
    this._visible.update((v) => !v);
  }

  public open(): void {
    this._visible.set(true);
  }

  public close(): void {
    this._visible.set(false);
  }

  public executeCommand(command: CommandInterface): void {
    this.close();

    if (command.action) {
      command.action();
    } else if (command.route) {
      if (Array.isArray(command.route)) {
        this._router.navigate(command.route);
      } else {
        this._router.navigate([command.route]);
      }
    }
  }

  public getCommands(): CommandInterface[] {
    return this._commands();
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
